/**
 * SHIPPER load status / dispatch-detail view.
 *
 * Visual clone of the approved shipper mockup, wired to LIVE data.
 * READ-ONLY for milestones: the shipper can never advance or undo a stage.
 * Milestones come from the SAME canonical derivation the broker view uses
 * (deriveStages); notes/documents/messaging reuse the existing shared flows.
 */
import { useMemo, useState } from "react";
import {
  ArrowLeft, MessageSquare, RefreshCw, Printer, Download, ChevronDown, CheckCircle2,
  Building2, User, Phone, Mail, CalendarDays, Truck, FileText,
  Loader2, LayoutPanelLeft, Gavel, Activity, Link2,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { DerivedStage } from "@/lib/lrDirect/deriveStages";
import { DispatchDocumentsPanel } from "@/components/lr-direct/DispatchDocumentsPanel";
import { LoadStageTracker } from "@/components/lr-direct/LoadStageTracker";
import { useLoadNotes, useMyCompanyName } from "@/hooks/useLoadNotes";
import { displayCompanyName } from "@/lib/displayCompanyName";

const TAB_META = [
  { key: "overview", label: "Overview", icon: LayoutPanelLeft },
  { key: "bids", label: "Bids", icon: Gavel },
  { key: "dispatch", label: "Dispatch", icon: Truck },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "timeline", label: "Timeline", icon: Activity },
  { key: "messages", label: "Messages", icon: MessageSquare },
] as const;


function fmtEta(iso: string | null | undefined) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function relativeAgo(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const STAGE_HINT: Record<string, string> = {
  posted: "The load is live on LR Direct and brokers can bid.",
  awarded: "You awarded this load. The broker is preparing dispatch details.",
  dispatch_created: "The dispatch packet exists. The broker will assign a truck next.",
  truck_en_route: "The truck is on its way to the first pickup.",
  in_transit: "The load is moving toward the delivery stop.",
  delivered: "All stops are complete. Waiting on paperwork.",
  pod_uploaded: "Proof of delivery is on file for this load.",
  closed: "This load is closed.",
};

function stageHint(stage: DerivedStage | null): string {
  if (!stage) return "Waiting on the next broker update.";
  if (STAGE_HINT[stage.key]) return STAGE_HINT[stage.key];
  if (stage.key.includes("checked_in") && stage.key.startsWith("pickup"))
    return "The truck arrived at the shipper and is waiting to be loaded. The broker will post the next update automatically.";
  if (stage.key.includes("loaded")) return "Freight is loaded and the truck is departing the pickup.";
  if (stage.key.startsWith("delivery_") && stage.key.includes("checked_in"))
    return "The truck arrived at the delivery stop.";
  if (stage.key.includes("delivered")) return "Freight was delivered at this stop.";
  return "Waiting on the next broker update.";
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-teal">
      {children}
    </div>
  );
}

export interface ShipperLoadStatusViewProps {
  load: {
    id: string;
    load_number: string;
    origin_city: string | null;
    origin_state: string | null;
    destination_city: string | null;
    destination_state: string | null;
    total_miles: number | null;
    awarded_at: string | null;
    created_by: string;
  };
  stages: DerivedStage[];
  awardedBroker: {
    company_name?: string | null;
    contact_name?: string | null;
    email?: string | null;
    phone?: string | null;
    user_id?: string | null;
  } | null;
  awardedAmount: number | null;
  dispatch: {
    id?: string | null;
    pickup_eta_at?: string | null;
    delivery_eta_at?: string | null;
    tracking_url?: string | null;
  } | null;
  viewerUserId: string;
  activeTab: string;
  onTabChange: (key: string) => void;
  onBack: () => void;
  onRefresh: () => void;
  onMessageBroker?: () => void;
  onOpenDispatchSheet?: () => void;
  /** Optional per-tab counts rendered as badges on the docked tab bar. */
  tabCounts?: Partial<Record<"bids" | "documents" | "messages", number>>;
  /**
   * Tab content rendered BELOW the docked tab bar. Everything above the bar is
   * identical on every tab; only this region swaps.
   */
  children?: React.ReactNode;
}

export function ShipperLoadStatusView({
  load, stages, awardedBroker, awardedAmount, dispatch, viewerUserId,
  activeTab, onTabChange, onBack, onRefresh, onMessageBroker, onOpenDispatchSheet,
  tabCounts, children,
}: ShipperLoadStatusViewProps) {

  const { notes, addNote } = useLoadNotes(load.id);
  const { data: myCompanyName } = useMyCompanyName(viewerUserId);
  const [text, setText] = useState("");

  const miles = load.total_miles != null ? Math.round(Number(load.total_miles)) : null;
  const rpm = awardedAmount != null && miles ? awardedAmount / miles : null;
  const currentStage = useMemo(
    () => stages.find((s) => s.status === "current") ?? null,
    [stages],
  );
  const lastDone = useMemo(
    () => [...stages].reverse().find((s) => s.status === "done") ?? null,
    [stages],
  );
  const statusLabel = (currentStage ?? lastDone)?.label ?? "Awarded";

  const pickupEta = fmtEta(dispatch?.pickup_eta_at);
  const deliveryEta = fmtEta(dispatch?.delivery_eta_at);
  const trackingUrl = dispatch?.tracking_url ?? null;

  const postNote = async () => {
    if (!text.trim() || addNote.isPending) return;
    try {
      await addNote.mutateAsync({
        body: text,
        authorUserId: viewerUserId,
        authorRole: "shipper",
        authorCompanyName: myCompanyName ?? null,
      });
      setText("");
    } catch (e: any) {
      toast.error(e?.message || "Could not post note");
    }
  };

  const brokerName = displayCompanyName(awardedBroker?.company_name) || awardedBroker?.company_name || "—";

  return (
    <div className="min-h-screen bg-lr-lighter-bg">
      {/* extra bottom room so a fixed cookie banner / iPhone home indicator can
          never sit on top of the tab bar or the last card */}
      <div className="mx-auto w-full max-w-[1400px] px-3 py-4 pb-[calc(env(safe-area-inset-bottom,0px)+7rem)] md:px-6 md:py-6 md:pb-10">
        {/* 1 — HEADER ROW */}
        <Card className="mb-4 border-border bg-card p-3 shadow-sm md:p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={onBack} aria-label="Back">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0">
                <h1 className="truncate text-xl font-bold tracking-tight text-foreground md:text-2xl">
                  Load {load.load_number}
                </h1>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground md:text-sm">
                  <span className="font-medium text-foreground/80">
                    {load.origin_city}, {load.origin_state}
                  </span>
                  <span aria-hidden>→</span>
                  <span className="font-medium text-foreground/80">
                    {load.destination_city}, {load.destination_state}
                  </span>
                  {miles != null && (
                    <>
                      <span aria-hidden>•</span>
                      <span>{miles.toLocaleString()} mi</span>
                    </>
                  )}
                  {load.awarded_at && (
                    <>
                      <span aria-hidden>•</span>
                      <span>
                        Awarded{" "}
                        {new Date(load.awarded_at).toLocaleDateString("en-US", {
                          month: "short", day: "numeric", year: "numeric",
                        })}
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
              <Button
                variant="outline" size="sm" className="h-11 gap-1.5 text-xs md:h-9"
                onClick={onMessageBroker} disabled={!onMessageBroker}
              >
                <MessageSquare className="h-4 w-4" /> Message
              </Button>
              <Button variant="outline" size="sm" className="h-11 gap-1.5 text-xs md:h-9" onClick={onRefresh}>
                <RefreshCw className="h-4 w-4" /> Refresh
              </Button>
              {/* Print is desktop-only (matches the broker view) — 5 controls wrap
                  to 3 rows at 375px otherwise. */}
              <Button variant="outline" size="sm" className="hidden h-9 gap-1.5 text-xs md:inline-flex" onClick={() => window.print()}>
                <Printer className="h-4 w-4" /> Print
              </Button>
              <Button
                variant="outline" size="sm" className="h-11 gap-1.5 text-xs md:h-9"
                onClick={onOpenDispatchSheet} disabled={!onOpenDispatchSheet}
              >
                <Download className="h-4 w-4" /> PDF
              </Button>
              <Button
                variant="outline" size="icon" className="h-11 w-11 md:h-9 md:w-9"
                onClick={() => onTabChange("dispatch")} aria-label="Open dispatch details"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>

          </div>
        </Card>

        {/* 2 — AWARD BANNER */}
        <Card className="mb-4 border-brand-teal/25 bg-brand-teal/[0.06] p-3 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                <CheckCircle2 className="h-4 w-4" />
              </span>
              <p className="text-sm">
                <span className="font-semibold text-foreground">This load has been awarded.</span>{" "}
                <span className="text-muted-foreground">
                  You can track broker and carrier progress in real time.
                </span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold tabular-nums text-foreground">
                {awardedAmount != null ? `$${awardedAmount.toLocaleString()}` : "—"}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {rpm != null ? `$${rpm.toFixed(2)}/mile` : ""}
              </span>
              <Button
                variant="outline" size="icon" className="h-8 w-8"
                onClick={() => onTabChange("bids")} aria-label="View bids"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* 3 — TWO SUMMARY CARDS */}
        <div className="mb-4 grid items-stretch gap-4 md:grid-cols-2">
          <Card className="h-full border-border bg-card p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-teal">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <SectionLabel>Awarded Broker &amp; Rate</SectionLabel>
                <div className="mt-2 grid gap-3 sm:grid-cols-[1.2fr_auto_auto]">
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Broker</p>
                    <p className="truncate text-sm font-bold text-foreground">{brokerName}</p>
                    <p className="text-xs text-muted-foreground">{awardedBroker?.contact_name ?? ""}</p>
                    <p className="truncate text-[11px] text-muted-foreground">
                      {[awardedBroker?.email, awardedBroker?.phone].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <div className="sm:border-l sm:border-border sm:pl-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Awarded Rate</p>
                    <p className="text-lg font-bold tabular-nums text-brand-teal">
                      {awardedAmount != null ? `$${awardedAmount.toLocaleString()}` : "—"}
                    </p>
                    <p className="text-[11px] text-muted-foreground tabular-nums">
                      {rpm != null ? `$${rpm.toFixed(2)}/mi · ` : ""}{miles != null ? `${miles.toLocaleString()} mi` : "—"}
                    </p>
                  </div>
                  <div className="sm:border-l sm:border-border sm:pl-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Status</p>
                    <span className="mt-1 inline-flex items-center rounded-md border border-brand-teal/30 bg-brand-teal/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-brand-teal">
                      {statusLabel}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <Card className="h-full border-border bg-card p-4 shadow-sm">
            <div className="flex h-full items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-teal">
                <User className="h-5 w-5 text-white" />
              </div>
              <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <SectionLabel>Broker Contact</SectionLabel>
                  {awardedBroker?.phone ? (
                    <a
                      href={`tel:${String(awardedBroker.phone).replace(/[^\d+]/g, "")}`}
                      className="mt-2 flex items-center gap-2 text-sm font-medium text-foreground hover:text-brand-teal"
                    >
                      <Phone className="h-4 w-4 text-muted-foreground" /> {awardedBroker.phone}
                    </a>
                  ) : (
                    <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="h-4 w-4" /> No phone on file
                    </p>
                  )}
                  {awardedBroker?.email ? (
                    <a
                      href={`mailto:${awardedBroker.email}`}
                      className="mt-1 flex items-center gap-2 truncate text-sm font-medium text-foreground hover:text-brand-teal"
                    >
                      <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{awardedBroker.email}</span>
                    </a>
                  ) : (
                    <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="h-4 w-4" /> No email on file
                    </p>
                  )}
                </div>
                <Button
                  variant="outline" size="sm" className="h-9 shrink-0 gap-1.5 text-xs"
                  onClick={onMessageBroker} disabled={!onMessageBroker}
                >
                  <MessageSquare className="h-4 w-4" /> Message Broker
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* 4 — LOAD PROGRESS (read-only) */}
        <Card className="mb-4 border-border bg-card p-4 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <CalendarDays className="h-4 w-4 text-brand-teal" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-foreground">Load Progress</h2>
            <span className="text-xs text-muted-foreground">Real-time status of this load</span>
          </div>

          {/* Mobile + desktop: reuse the canonical dispatch tracker. The shipper
              view is read-only — no renderStageButton is passed, so milestones
              cannot be advanced. The current stage is highlighted as "Next up"
              to match the broker/carrier orange treatment. */}
          <div className="md:hidden">
            <LoadStageTracker
              appearance="dispatch"
              stages={stages}
              nextKey={currentStage?.key ?? null}
            />
          </div>

          <div className="hidden md:block -mx-1 overflow-x-auto px-1 pb-1 pt-2 [scrollbar-width:thin]">
            <LoadStageTracker
              appearance="dispatch"
              stages={stages}
              nextKey={currentStage?.key ?? null}
            />
          </div>

          {/* current status summary strip */}
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-lr-orange/30 bg-lr-orange-subtle p-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-lr-orange/15">
              <Truck className="h-4 w-4 text-lr-orange" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">Current: {statusLabel}</p>
              <p className="text-xs text-muted-foreground">{stageHint(currentStage ?? lastDone)}</p>
            </div>
          </div>
        </Card>

        {/* 5 — FOUR MINI CARDS */}
        <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            {
              icon: Truck,
              label: "Truck ETA to Pickup",
              value: pickupEta ?? "Awaiting broker ETA",
              muted: !pickupEta,
            },
            {
              icon: CalendarDays,
              label: "Estimated Delivery",
              value: deliveryEta ?? "Awaiting broker ETA",
              muted: !deliveryEta,
            },
            {
              icon: Link2,
              label: "External Tracking Link",
              value: trackingUrl ? "Carrier live tracking" : "No tracking link yet",
              muted: !trackingUrl,
              action: trackingUrl ? "Open Link" : undefined,
              onAction: trackingUrl
                ? () => window.open(trackingUrl, "_blank", "noopener")
                : undefined,
            },
            {
              icon: FileText,
              label: "Load Notes",
              value: `${notes.length} ${notes.length === 1 ? "note" : "notes"}`,
              muted: notes.length === 0,
              action: "View Notes",
              onAction: () =>
                document.getElementById("shipper-load-notes")?.scrollIntoView({ behavior: "smooth", block: "center" }),
            },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <Card key={c.label} className="h-full border-border bg-card p-3 shadow-sm">
                <div className="flex items-start gap-2.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-teal/10">
                    <Icon className="h-4 w-4 text-brand-teal" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[10px] font-bold uppercase tracking-[0.14em] text-brand-teal">
                      {c.label}
                    </p>
                    <p
                      className={cn(
                        "mt-1 truncate text-sm",
                        c.muted ? "text-muted-foreground" : "font-semibold text-foreground"
                      )}
                      title={typeof c.value === "string" ? c.value : undefined}
                    >
                      {c.value}
                    </p>
                    {c.action && (
                      <Button
                        variant="outline" size="sm" className="mt-2 h-11 w-full text-xs md:h-7"
                        onClick={c.onAction}
                      >
                        {c.action}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* 6/7/8 — NOTES + DOCUMENTS */}
        <div className="mb-4 grid items-start gap-4 lg:grid-cols-2">
          <Card id="shipper-load-notes" className="border-border bg-card p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-brand-teal" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-foreground">Load Notes</h2>
            </div>
            <div className="space-y-2">
              {notes.length === 0 ? (
                <p className="py-2 text-xs text-muted-foreground">
                  No notes yet. Add a note about this load — both you and the broker will see it.
                </p>
              ) : (
                notes.map((n) => (
                  <div
                    key={n.id}
                    className="flex items-start justify-between gap-3 rounded-lg border border-border bg-lr-surface/60 p-3"
                  >
                    <div className="flex min-w-0 items-start gap-2.5">
                      <span className="mt-0.5 shrink-0 rounded-md border border-border bg-card px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                        {n.author_role === "shipper" ? "SHIPPER" : "BROKER"}
                      </span>
                      <p className="min-w-0 whitespace-pre-wrap break-words text-sm text-foreground">{n.body}</p>
                    </div>
                    <span className="shrink-0 text-[11px] text-muted-foreground">{relativeAgo(n.created_at)}</span>
                  </div>
                ))
              )}
            </div>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
              <Textarea
                rows={3}
                value={text}
                maxLength={2000}
                onChange={(e) => setText(e.target.value)}
                placeholder="Add a note for the broker (status update, instructions, gate code...)"
                className="resize-none text-sm"
              />
              <Button
                className="h-9 shrink-0 bg-lr-orange text-white hover:bg-lr-orange-hover"
                disabled={!text.trim() || addNote.isPending}
                onClick={postNote}
              >
                {addNote.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Post note
              </Button>
            </div>
          </Card>

          <DispatchDocumentsPanel
            dispatchId={dispatch?.id ?? undefined}
            loadId={load.id}
            uploaderId={viewerUserId}
            counterpartyUserId={awardedBroker?.user_id ?? null}
            canUpload
            shipperUserId={load.created_by}
            cardClassName="border-border bg-card shadow-sm"
            onChanged={onRefresh}
          />
        </div>

        {/* 9 — BOTTOM TABS */}
        <nav
          aria-label="Load sections"
          className="relative rounded-xl border border-border bg-card p-1.5 shadow-sm md:p-2"
        >
          {/* right-edge fade: makes it obvious the 6-tab strip scrolls at 375px */}
          <div className="pointer-events-none absolute right-1.5 top-1.5 bottom-1.5 z-10 w-8 rounded-r-lg bg-gradient-to-l from-card to-transparent md:hidden" />
          <div className="flex items-center gap-1.5 overflow-x-auto px-0.5 pr-6 [scrollbar-width:none] md:pr-0 [&::-webkit-scrollbar]:hidden">
            {TAB_META.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => onTabChange(t.key)}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex h-11 flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-3.5 text-sm font-medium md:px-4",

                    active
                      ? "bg-lr-orange font-semibold text-white shadow-md"
                      : "border border-border bg-background text-foreground hover:border-lr-orange/40 hover:bg-lr-orange-subtle hover:text-lr-orange"
                  )}
                >
                  <Icon className={cn("h-4 w-4", active ? "text-white" : "text-muted-foreground")} />
                  {t.label}
                  {(() => {
                    const n = tabCounts?.[t.key as "bids" | "documents" | "messages"];
                    if (!n) return null;
                    return (
                      <span
                        className={cn(
                          "ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold tabular-nums",
                          active ? "bg-white/20 text-white" : "bg-muted text-muted-foreground",
                        )}
                      >
                        {n}
                      </span>
                    );
                  })()}
                </button>
              );
            })}
          </div>
        </nav>

        {/* 10 — TAB CONTENT (only region that swaps; chrome above never changes) */}
        {children ? (
          <div className="shipper-tab-pane mt-4 motion-safe:animate-fade-in">
            <Card className={cn(
              "border-border bg-card shadow-sm md:p-4",
              /* the Dispatch tab nests a full sheet — trim mobile padding so the
                 field grid keeps usable width at 375px */
              activeTab === "dispatch" ? "p-1.5" : "p-3",
            )}>
              <div className="mb-3 flex items-center gap-2">
                {(() => {
                  const meta = TAB_META.find((t) => t.key === activeTab);
                  const Icon = meta?.icon ?? LayoutPanelLeft;
                  return <Icon className="h-4 w-4 text-lr-orange" />;
                })()}
                <h2 className="text-sm font-bold uppercase tracking-wider text-foreground">
                  {TAB_META.find((t) => t.key === activeTab)?.label ?? ""}
                </h2>
              </div>
              {children}
            </Card>
          </div>
        ) : null}
      </div>
    </div>
  );
}


export default ShipperLoadStatusView;
